import { classes, stylesheet } from 'typestyle';
import {
  Blue0,
  Gray100,
  Gray140,
  Gray20,
  Green90,
  Red10,
  Red80,
  typeCompact12MediumProperties,
  typeCompact14MediumProperties,
  White,
  WhiteOpacity70,
} from '@traceair/tds';
import React from 'react';
import { MissionData } from '../../types';
import { useUploadStore } from '../../../../../store/uploadStore';
import { CalculatedUploadState, useCalculatedUploadState } from './useCalculatedUploadState';
import { getProgressUiElements } from '../../openMission/flightUpload/uploadProgress/flightUploadProgress/getProgressUiElements';
import { getUploadProgress } from '../../helpers/uploadProgress';
import { getUploadValidationStatus } from '../../helpers/uploadValidation';
import { UploadValidationStatus } from '../../../../../consts/uploadValidationStatus';

const css = stylesheet({
  container: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    marginTop: 8,
    height: 24,
    padding: '0 8px',
    border: `1px solid ${Gray20}`,
    borderRadius: 8,
    color: Gray140,
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    ...typeCompact14MediumProperties,
  },
  containerCompleted: {
    $nest: {
      '&&': {
        color: White,
      },
    },
  },
  containerInner: {
    position: 'relative',
    zIndex: 2,
  },
  note: {
    position: 'relative',
    zIndex: 2,
    marginLeft: 2,
    color: Gray100,
    ...typeCompact12MediumProperties,
  },
  noteCompleted: {
    $nest: {
      '&&': {
        color: WhiteOpacity70,
      },
    },
  },
  progressBar: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
  },
});

interface Props extends MissionData {
  isPano: boolean;
  validationStatus: UploadValidationStatus;
}

export const MissionUploadProgress = ({ isPano, customer, site, schedule, validationStatus }: Props) => {
  const { getUpload, getRegistrationRequests } = useUploadStore();

  const registrationRequests = getRegistrationRequests(isPano, customer.id, site.id, schedule?.airtableRecordId);
  const upload = getUpload(isPano, customer.id, site.id, schedule?.airtableRecordId);

  const uploadState = useCalculatedUploadState(isPano, customer.id, site.id, schedule?.airtableRecordId);
  const { bytesPerSecond, fileRegistrationTimeMs } = useUploadStore();

  if (!registrationRequests?.size && !upload?.overview) {
    return;
  }

  // Don't show progress for an interrupted upload that hasn't been restored yet
  if (!upload?.upload) {
    return null;
  }

  // Don't show progress for a previously completed upload
  if (upload?.meta.isPreviouslyCompleted) {
    return null;
  }

  const { note, shortNote } = getProgressUiElements(
    uploadState,
    registrationRequests,
    upload,
    bytesPerSecond,
    fileRegistrationTimeMs,
  );

  const progress = Math.round(getUploadProgress(upload));
  const isCompleted = uploadState === CalculatedUploadState.Completed;

  let title = isPano ? 'Pano' : 'Scan';
  if (validationStatus === UploadValidationStatus.HasErrors) {
    title += ' with validation issues';
  }

  let progressColor = validationStatus === UploadValidationStatus.HasErrors ? Red10 : Blue0;
  if (isCompleted) {
    if (validationStatus === UploadValidationStatus.HasErrors) {
      progressColor = Red80;
    } else {
      progressColor = Green90;
    }
  }

  return (
    <div className={classes(css.container, isCompleted && css.containerCompleted)}>
      <div className={css.containerInner}>{title} ∙</div>
      <div className={classes(css.note, isCompleted && css.noteCompleted)}>{shortNote ?? note}</div>

      <div className={classes(css.progressBar)} style={{ width: `${progress}%`, background: progressColor }} />
    </div>
  );
};
